import { useState } from "react";
import {
  Button, Chip, ColorField, Disclosure, FileButton, NumberField, OptionCard,
  SectionLabel, Select, Slider, Swatch, SwatchRow, TextField, Toggle, hexOf,
} from "../../kit";
import { useCaps } from "../../editor/CapsProvider";
import { useRing } from "../ringContext";
import { downloadJSON } from "../../../util/download";
import { MAX_PARTITIONS, normalizeRatios, splitModeForCount } from "../../../shared/api-contract";
import { RATIO_FLOOR, evenRatios, maxShare, patternRatios, rebalance, sectionsAreArcs } from "../ratios";
import { BalanceBars, PartitionIcon } from "../icons";
import type {
  DivisionStyle, MetalParamName, PartitionMaterial, SplitMode,
} from "../../../shared/api-contract";

/**
 * The reference's five partition tiles. The number IS the option — `SplitMode`
 * is only a label for it (see the contract's note on the type), so these drive
 * `setPartitionCount` and never re-derive a count from a mode.
 *
 * "Multi" means five or more: pressing it lands on five, and the stepper that
 * appears while it is active walks up to `MAX_PARTITIONS`.
 */
const PARTITIONS: { count: number; label: string }[] = [
  { count: 1, label: "Single" },
  { count: 2, label: "Duo" },
  { count: 3, label: "Tri" },
  { count: 4, label: "Quad" },
  { count: 5, label: "Multi" },
];

/** Pre-v42 viewers only know `setSplitMode`, which stops at three partitions. */
const LEGACY_MODE_FOR_COUNT: Record<number, SplitMode> = { 1: "none", 2: "two-tone", 3: "tricolor" };

/** Used only when the connected viewer predates `Catalogs.divisionStyles` (v37). */
const DIVISION_FALLBACK: { id: DivisionStyle; label: string }[] = [
  { id: "vertical", label: "Vertical" },
  { id: "axial", label: "Axial" },
  { id: "diagonal", label: "Diagonal" },
  { id: "wavy", label: "Wavy" },
];

/** Baseline values shown until a partition override is set (state is the
 * source of truth; once edited, the override value drives the slider). */
const METAL_BASELINE: Record<MetalParamName, number> = {
  roughness: 0.2,
  metalness: 1,
  envMapIntensity: 1.8,
  ior: 1.45,
  clearcoat: 0,
  clearcoatRoughness: 0,
  specularIntensity: 1,
  sheen: 0,
  anisotropy: 0,
};

const ADVANCED: { name: MetalParamName; label: string; min: number; max: number; step: number }[] = [
  { name: "roughness", label: "Roughness", min: 0, max: 1, step: 0.01 },
  { name: "metalness", label: "Metalness", min: 0, max: 1, step: 0.01 },
  { name: "envMapIntensity", label: "Env Intensity", min: 0, max: 5, step: 0.01 },
  { name: "ior", label: "IOR", min: 1.0, max: 2.5, step: 0.01 },
  { name: "clearcoat", label: "Clearcoat", min: 0, max: 1, step: 0.01 },
  { name: "clearcoatRoughness", label: "Clearcoat Roughness", min: 0, max: 1, step: 0.01 },
  { name: "specularIntensity", label: "Specular Intensity", min: 0, max: 2, step: 0.01 },
  { name: "sheen", label: "Sheen", min: 0, max: 1, step: 0.01 },
  { name: "anisotropy", label: "Anisotropy", min: 0, max: 1, step: 0.01 },
];

/**
 * StoneAlgo's karat-labeled swatch grid. Our `PartitionMaterial` only carries a
 * `presetId` + `finishId` — karat is not a shading input, so each label maps
 * onto one of the five engine metal presets and the karat is display-only.
 * (Inlined from `ui/engagement/metalColors.ts`, which a later cleanup wave deletes.)
 */
const METAL_COLOR_OPTIONS: { id: string; label: string; presetId: string }[] = [
  { id: "14k-white", label: "14K White", presetId: "whiteGold" },
  { id: "18k-yellow", label: "18K Yellow", presetId: "yellowGold" },
  { id: "14k-rose", label: "14K Rose", presetId: "roseGold" },
  { id: "18k-white", label: "18K White", presetId: "whiteGold" },
  { id: "platinum", label: "Platinum", presetId: "platinum" },
  { id: "18k-rose", label: "18K Rose", presetId: "roseGold" },
  { id: "14k-yellow", label: "14K Yellow", presetId: "yellowGold" },
];

const DEFAULT_FINISH_ID = "polished";

/**
 * The reference's Surface row names the straight unidirectional finish "Linear";
 * our engine ships that pattern as `satin` (fine parallel lines) and has no
 * `linear` id, so the chip carries their word for it. Stands down the moment the
 * catalog serves a real `linear` finish, so the row can never show two Linears.
 *
 * ponytail: the durable fix is one word in the engine's METAL_FINISHES (`satin`
 * → label "Linear"), which would also fix the spec sheet — that file is not this
 * agent's to edit, so the spec sheet still prints "Satin".
 */
const finishLabel = (finish: { id: string; label: string }, catalogHasLinear: boolean) =>
  !catalogHasLinear && finish.id === "satin" ? "Linear" : finish.label;

/**
 * Ring Builder → Material tab. The band metal step (purity, colour scheme,
 * split pattern, per-partition panels) plus, when the engagement head is
 * attached, the karat swatch grid and the head-vs-band "Mixed" split.
 */
export function MaterialTab() {
  const { state, catalogs, r, apply, engagementOn } = useRing();
  const caps = useCaps();
  const divisions = catalogs.divisionStyles?.length ? catalogs.divisionStyles : DIVISION_FALLBACK;

  return (
    <div className="flex flex-col gap-5">
      {engagementOn ? <EngagementMetalGrid /> : null}

      <div className="flex flex-col gap-2">
        <SectionLabel>Metal Purity</SectionLabel>
        <Select
          value={state.metalKarat}
          options={catalogs.metalKarats}
          onChange={(id) => void apply(r.setMetalKarat(id))}
          label="Metal karat"
        />
      </div>

      <Partitions />

      {state.splitMode !== "none" ? (
        <>
          <Balance />

          {/* v37: how the seam between partitions travels around the ring. */}
          {caps.ringBuilderV37 ? (
            <div className="flex flex-col gap-2">
              <SectionLabel>Seam Path</SectionLabel>
              <div className="grid grid-cols-4 gap-2">
                {divisions.map((d) => (
                  <Chip
                    key={d.id}
                    selected={(state.division ?? "vertical") === d.id}
                    onClick={() => void apply(r.setDivision(d.id as DivisionStyle))}
                  >
                    {d.label}
                  </Chip>
                ))}
              </div>
            </div>
          ) : null}

          {caps.ringLongTailV41 ? <CustomBalance /> : null}
        </>
      ) : null}

      {/* One panel per section. Their tab strip is `Metal 1 … Metal N` at every
          count including Single, so an un-renamed panel says the same — and it
          matches the rename field's own placeholder. */}
      {state.materials.map((pm, i) => (
        <MetalPanel key={i} index={i} mat={pm} title={pm.label ?? `Metal ${i + 1}`} />
      ))}

      {/* v38: the inner wall keeps its partition colour but goes polished,
          whatever surface finish the outside is wearing. */}
      {caps.ringGeometryV38 ? (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="font-display text-[11px] font-medium uppercase tracking-[0.12em] text-on-surface-variant">
              Inside Polished
            </span>
            <Toggle
              // v44 defaults this ON and always serializes the flag, so the
              // fallback is only ever hit by a pre-v43 viewer — which does not
              // split the inner faces at all, i.e. OFF is what it is showing.
              checked={state.insidePolished ?? false}
              onChange={(v) => void apply(r.setInsidePolished(v))}
              label="Polished inner surface"
            />
          </div>
          <span className="-mt-3 font-body text-[10px] text-outline">
            Keeps each partition's metal, forces its inner face to a polished finish.
          </span>
        </>
      ) : null}
    </div>
  );
}

/**
 * v42 "Partition" — how many metals the band is cut into, as icon tiles.
 *
 * Each tile draws the band face-on with the seams that count produces, from
 * `PartitionIcon`: the SELECTED tile gets the ring's live ratios (so a Custom
 * Balance or an uneven preset shows in the glyph), the rest get even ones,
 * which is exactly what `setPartitionCount(n)` lands on — every catalog pattern
 * with n ratios is even, as is the synthesised `nN-even` past five.
 *
 * A pre-v42 viewer has no `setPartitionCount`: the first three tiles fall back
 * to `setSplitMode` (which is all that mode could ever express) and Quad/Multi
 * render disabled rather than storing a mode with no pattern behind it.
 */
function Partitions() {
  const { state, catalogs, r, apply } = useRing();
  const caps = useCaps();
  const count = state.materials.length;
  const v42 = caps.ringWaveFV42;
  // The glyph draws the axis the geometry cuts on, which since `division` took
  // ownership of it is the width for every division but Axial.
  const arcs = sectionsAreArcs(state.division, state.splitPatternId);
  const pattern = catalogs.splitPatterns.find((p) => p.id === state.splitPatternId);
  const live = state.customRatios?.length
    ? normalizeRatios(state.customRatios, count)
    : patternRatios(pattern, count);

  const pick = (n: number) => {
    // "Multi" owns every count from five up, so pressing it at seven is a
    // press on the tile already selected — not a request to drop to five.
    if (n === count || (n === 5 && count > 5)) return;
    if (v42) return void apply(r.setPartitionCount(n));
    const mode = LEGACY_MODE_FOR_COUNT[n];
    if (mode) void apply(r.setSplitMode(mode));
  };

  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>Partition</SectionLabel>
      <div className="grid grid-cols-5 gap-2">
        {PARTITIONS.map((p) => {
          // "Multi" owns every count from five up, so it stays lit at seven.
          const selected = p.count === 5 ? count >= 5 : count === p.count;
          const usable = v42 || !!LEGACY_MODE_FOR_COUNT[p.count];
          const shown = selected ? live : evenRatios(p.count);
          if (!usable) {
            return (
              <span
                key={p.count}
                title="Requires viewer contract v42+"
                className="flex cursor-not-allowed flex-col items-center justify-center gap-1.5 rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-2 opacity-40"
              >
                <PartitionIcon ratios={evenRatios(p.count)} horizontal={!arcs} />
                <span className="font-display text-[8px] uppercase tracking-[0.1em] text-on-surface-variant">
                  {p.label}
                </span>
              </span>
            );
          }
          return (
            <OptionCard
              key={p.count}
              selected={selected}
              title={`${p.label} — ${p.count === 5 ? "five or more" : p.count} metal${p.count > 1 ? "s" : ""}`}
              onClick={() => pick(p.count)}
            >
              <span className="text-primary">
                <PartitionIcon ratios={shown} horizontal={!arcs} />
              </span>
              <span className="font-display text-[8px] uppercase tracking-[0.1em] text-on-surface-variant">
                {p.label}
              </span>
            </OptionCard>
          );
        })}
      </div>
      {v42 && count >= 5 ? (
        <Slider
          label="Metals"
          min={5}
          max={MAX_PARTITIONS}
          step={1}
          ticks={MAX_PARTITIONS - 4}
          value={count}
          display={(v) => `${Math.round(v)} partitions`}
          // Not draft-batched as a drag: each step is a whole pattern change and
          // a materials-list resize, not a dimension being scrubbed.
          draft={false}
          onChange={(v) => void apply(r.setPartitionCount(Math.round(v)))}
        />
      ) : null}
      {v42 && count > 5 ? (
        <span className="-mt-2 font-body text-[10px] text-outline">
          Past five the split is generated evenly — {MAX_PARTITIONS} is the manufacturing limit
          (thinner seams than the saw).
        </span>
      ) : null}
    </div>
  );
}

/**
 * "Balance" — which of the count's presets the metals are cut on, drawn as the
 * ratio bars they are.
 *
 * The bar comes from `patternRatios`, the SAME source Custom
 * Balance seeds itself from, so a tile and the sliders it opens onto can never
 * disagree. Each bar stacks along the axis its own seams run on
 * (`sectionsAreArcs`).
 *
 * Five to a row, because the catalog leads with the reference's five named
 * balances (1:1 · 2:1 · 3:1 · 1:2 · 1:3) and our extra tiles flow underneath —
 * so their row of five reads as a row of five without hiding ours.
 */
function Balance() {
  const { state, catalogs, r, apply } = useRing();
  const count = state.materials.length;
  // The engine labels the count, so ask it the same question rather than
  // filtering on `state.splitMode` — which for a generated `nN-even` pattern
  // is "multi" while the tiles for five, six and seven all live under it.
  // Catalog modes map 1:1 onto counts 1-5 ("multi" is the five-partition
  // presets); past five there is no preset to offer — `setPartitionCount`
  // synthesises an even split and Custom Balance is how it gets shaped.
  const mode = splitModeForCount(count);
  const patterns = count > 5 ? [] : catalogs.splitPatterns.filter((p) => p.mode === mode);

  if (patterns.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        <SectionLabel>Balance</SectionLabel>
        <span className="font-body text-[10px] text-outline">
          {count} partitions are split evenly — set the shares by hand with Custom Balance below.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>Balance</SectionLabel>
      <div className="grid grid-cols-5 gap-2">
        {patterns.map((pat) => (
          <OptionCard
            key={pat.id}
            selected={state.splitPatternId === pat.id}
            title={pat.label}
            onClick={() => void apply(r.setSplitPattern(pat.id))}
            className="aspect-square"
          >
            <BalanceBars
              ratios={patternRatios(pat, count)}
              horizontal={!sectionsAreArcs(state.division, pat.id)}
            />
            <span className="w-full truncate text-center font-body text-[8px] tracking-wide text-on-surface-variant">
              {pat.label}
            </span>
          </OptionCard>
        ))}
      </div>
      {state.customRatios?.length ? (
        <span className="font-body text-[10px] text-outline">
          Custom Balance is on — it overrides whichever pattern is selected here.
        </span>
      ) : null}
    </div>
  );
}

/**
 * v41 Custom Balance — free partition widths in place of the split pattern's
 * preset ratios. Off (`customRatios` absent) the pattern rules, exactly as
 * before; the toggle seeds the sliders with the ratios already on the ring.
 *
 * Moving one slider holds that partition at the value shown and rescales the
 * others proportionally, so the engine's normalisation cannot bounce the thumb
 * back the moment it lands.
 *
 * A share is stored as a fraction but read out in the unit of the axis it is a
 * share of — the reference's per-section width in mm on every division but
 * Axial, where the sections are arcs and a mm figure would be a fiction, so
 * those read in degrees of arc. All N at once, no `− Section N +` stepper: the
 * stepper exists to reach the sections their one-slider layout hides.
 */
function CustomBalance() {
  const { state, catalogs, r, apply } = useRing();
  const count = state.materials.length;
  if (count < 2) return null;

  const stored = state.customRatios;
  const on = !!stored?.length;
  const pattern = catalogs.splitPatterns.find((p) => p.id === state.splitPatternId);
  const ratios = on ? normalizeRatios(stored, count) : patternRatios(pattern, count);
  const arcs = sectionsAreArcs(state.division, state.splitPatternId);
  const span = arcs ? 360 : state.widthMM;

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <SectionLabel>Custom Balance</SectionLabel>
        <Toggle
          checked={on}
          onChange={(v) => void apply(r.setCustomRatios(v ? ratios : null))}
          label="Set each metal's share by hand"
        />
      </div>
      {on ? (
        ratios.map((share, i) => (
          <Slider
            key={i}
            label={state.materials[i]?.label ?? `Metal ${i + 1}`}
            min={RATIO_FLOOR * span}
            max={maxShare(count) * span}
            step={arcs ? 1 : 0.05}
            // The reference's own ruler marks 1 mm and 2 mm; `ticks` drops
            // either if the band is too narrow to reach it.
            ticks={arcs ? undefined : [1, 2]}
            display={(v) => (arcs ? `${Math.round(v)}°` : `${v.toFixed(2)} mm`)}
            value={share * span}
            onChange={(v) => void r.setCustomRatios(rebalance(ratios, i, v / span))}
          />
        ))
      ) : (
        <span className="-mt-3 font-body text-[10px] text-outline">
          Off: the split pattern{pattern ? ` (${pattern.label})` : ""} sets the balance.
        </span>
      )}
    </>
  );
}

/**
 * "Head & Band Colour" karat grid. The head is `engagement.head.metal`; the
 * band half is every `state.materials` partition (the old engagement shank had
 * exactly one metal — in the merged builder the split modes can give it up to
 * three, and this grid means "the whole band", matching its label).
 */
function EngagementMetalGrid() {
  const { state, catalogs, r, apply } = useRing();
  const head = state.engagement?.head.metal;
  const [mixed, setMixed] = useState(head?.presetId !== state.materials[0]?.presetId);

  if (!head) return null;

  const hexFor = (presetId: string) => {
    const preset = catalogs.metalPresets.find((p) => p.id === presetId);
    return preset ? hexOf(preset.color) : "#c9c9c9";
  };

  /** Existing finishes are preserved — unlike the pre-merge engagement grid,
   * the band panels below can set a finish and this must not clobber it. */
  const setHead = (presetId: string) => r.setHeadMetal(presetId, head.finishId ?? DEFAULT_FINISH_ID);
  const setBand = async (presetId: string) => {
    for (let i = 0; i < state.materials.length; i++) {
      await apply(r.setPartitionMaterial(i, presetId, state.materials[i].finishId ?? DEFAULT_FINISH_ID));
    }
  };

  const bandIs = (presetId: string) => state.materials.every((m) => m.presetId === presetId);

  return (
    <>
      <div className="flex flex-col gap-2">
        <SectionLabel>Head &amp; Band Colour</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          {METAL_COLOR_OPTIONS.map((opt) => (
            <SwatchRow
              key={opt.id}
              hex={hexFor(opt.presetId)}
              label={opt.label}
              selected={!mixed && head.presetId === opt.presetId && bandIs(opt.presetId)}
              onClick={() => {
                setMixed(false);
                void (async () => { await setHead(opt.presetId); await setBand(opt.presetId); })();
              }}
            />
          ))}
          <button
            type="button"
            title="Set the head and band to different metals"
            onClick={() => setMixed((m) => !m)}
            className={`flex items-center justify-center rounded-xl border px-3 py-2.5 transition-colors ${
              mixed
                ? "border-primary bg-surface-container-high/40"
                : "border-outline-variant/40 bg-surface-container-lowest hover:border-outline"
            }`}
          >
            <span className="font-body text-[11px] font-semibold text-secondary">Mixed</span>
          </button>
        </div>
      </div>

      {mixed ? (
        <>
          <div className="flex flex-col gap-2">
            <SectionLabel>Head Colour</SectionLabel>
            <div className="grid grid-cols-2 gap-2">
              {METAL_COLOR_OPTIONS.map((opt) => (
                <SwatchRow
                  key={opt.id}
                  hex={hexFor(opt.presetId)}
                  label={opt.label}
                  selected={head.presetId === opt.presetId}
                  onClick={() => void apply(setHead(opt.presetId))}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <SectionLabel>Band Colour</SectionLabel>
            <div className="grid grid-cols-2 gap-2">
              {METAL_COLOR_OPTIONS.map((opt) => (
                <SwatchRow
                  key={opt.id}
                  hex={hexFor(opt.presetId)}
                  label={opt.label}
                  selected={bandIs(opt.presetId)}
                  onClick={() => void setBand(opt.presetId)}
                />
              ))}
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}

function MetalPanel({ index, mat, title }: { index: number; mat: PartitionMaterial; title: string }) {
  const { catalogs, r, apply, refresh } = useRing();
  const caps = useCaps();
  const ov = mat.override ?? {};
  const overridden = Object.keys(ov).filter((k) => k !== "color").length;
  const hasLinear = catalogs.metalFinishes.some((f) => f.id === "linear");

  const handleImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => void apply(r.importBandMetalJSON(index, String(reader.result)));
    reader.readAsText(file);
  };

  const handleExport = async () => {
    const json = await r.exportBandMetalJSON(index);
    if (json) downloadJSON(json, `metal-material-${index + 1}.json`);
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-4">
      {caps.ringLongTailV41 ? (
        // v41: the partition's name is the panel heading. Blank restores "Metal N".
        <TextField
          value={title}
          maxLength={24}
          placeholder={`Metal ${index + 1}`}
          onChange={(v) => void apply(r.setPartitionLabel(index, v.trim() || null))}
          // Not `uppercase`: that would show a name the spec sheet then prints
          // back in the case it was actually typed in.
          className="!px-2 !py-1.5 font-display !text-[12px] font-semibold tracking-[0.06em]"
        />
      ) : (
        <span className="font-display text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant">
          {title}
        </span>
      )}

      <div className="flex flex-wrap gap-3">
        {catalogs.metalPresets.map((preset) => (
          <Swatch
            key={preset.id}
            color={preset.color}
            label={preset.label}
            selected={preset.id === mat.presetId && !ov.color}
            onClick={() => void apply(r.setPartitionMaterial(index, preset.id, mat.finishId))}
          />
        ))}
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="font-body text-[10px] text-outline">Surface Finish</span>
        <div className="flex flex-wrap gap-2">
          {catalogs.metalFinishes.map((finish) => (
            <Chip
              key={finish.id}
              selected={finish.id === mat.finishId}
              onClick={() => void apply(r.setPartitionMaterial(index, mat.presetId, finish.id))}
            >
              {finishLabel(finish, hasLinear)}
            </Chip>
          ))}
        </div>
      </div>

      <ColorField
        label="Custom Colour"
        value={ov.color ?? "#ffd9a0"}
        onChange={(hex) => void apply(r.setPartitionMetalColor(index, hex))}
      />

      <Disclosure label="Advanced" value={overridden ? `${overridden} overridden` : "Default"}>
        {ADVANCED.map((p) => (
          <NumberField
            key={p.name}
            label={p.label}
            min={p.min}
            max={p.max}
            step={p.step}
            value={ov[p.name] ?? METAL_BASELINE[p.name]}
            onChange={(v) => { void r.setPartitionMetalParam(index, p.name, v); void refresh(); }}
          />
        ))}

        <div className="mt-1 flex gap-2">
          <Button variant="outline" full onClick={handleExport}>Export JSON</Button>
          <FileButton accept="application/json,.json" full onFile={handleImport}>Import JSON</FileButton>
        </div>
      </Disclosure>
    </div>
  );
}

export default MaterialTab;
